import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useTiktokPageShopScope, stackShopDimensionTabs } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import TiktokDateRangeFilter from '@/tiktok-platform/components/TiktokDateRangeFilter';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

function pickData(res) {
  if (!res || typeof res !== 'object') return {};
  if (res.data && typeof res.data === 'object') return res.data;
  return res;
}

function formatMoney(amount, currency) {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  const cur = currency ? ` ${currency}` : '';
  if (Number.isFinite(n)) return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${cur}`;
  return `${amount}${cur}`;
}

function moneyOf(row, key) {
  const value = row?.[key];
  if (value && typeof value === 'object') {
    return formatMoney(value.value ?? value.amount, value.currency || row.currency || '');
  }
  return formatMoney(value, row?.currency || '');
}

function formatTime(raw) {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    const ms = n < 1e12 ? n * 1000 : n;
    try {
      return new Date(ms).toLocaleString('zh-CN', { hour12: false });
    } catch {
      return String(raw);
    }
  }
  return String(raw).replace('T', ' ').slice(0, 19);
}

function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 89);
  return { start: isoDate(start), end: isoDate(end) };
}

const PAYMENT_STATUS_LABELS = {
  PAID: '已付款',
  FAILED: '失败',
  PROCESSING: '处理中',
};

const WITHDRAW_TYPES = [
  { value: 'WITHDRAW', labelKey: 'finance.withdraw' },
  { value: 'SETTLE', labelKey: 'finance.settleIn' },
  { value: 'TRANSFER', labelKey: 'finance.transfer' },
  { value: 'REVERSE', labelKey: 'finance.reverse' },
];

const FINANCE_TABS = [
  { id: 'payments', labelKey: 'finance.tabPayments' },
  { id: 'statements', labelKey: 'finance.tabStatements' },
  { id: 'unsettled', labelKey: 'finance.unsettled' },
  { id: 'withdrawals', labelKey: 'finance.tabWithdrawals' },
];

function listKey(tab) {
  if (tab === 'statements') return ['statements', 'statement_list'];
  if (tab === 'withdrawals') return ['withdrawals', 'withdrawal_list'];
  if (tab === 'unsettled') return ['transactions', 'unsettled_transactions'];
  return ['payments', 'payment_list'];
}

function paymentStatusTag(code) {
  if (!code) return '—';
  const fallback = PAYMENT_STATUS_LABELS[code] || code;
  const key = code === 'PAID' ? 'finance.stCheck3' : (code === 'FAILED' ? 'certLib.failedShort' : (code === 'PROCESSING' ? 'finance.processing' : ''));
  const label = key ? i18n.t(key, { ns: 'workbench', defaultValue: fallback }) : fallback;
  const tone = code === 'PAID' ? 'ok' : (code === 'FAILED' ? 'fail' : 'pending');
  return <span className={`tko-tag tko-tag--${tone}`}>{label}</span>;
}

function rowKey(row, tab, index = 0) {
  if (tab === 'statements') {
    return String(row?.id || row?.statement_id || `statement-${index}`);
  }
  if (tab === 'withdrawals') {
    return String(row?.id || row?.withdrawal_id || `withdrawal-${index}`);
  }
  if (tab === 'unsettled') {
    return String(row?.id || row?.order_id || row?.adjustment_id || `unsettled-${index}`);
  }
  return String(row?.id || row?.payment_id || `payment-${index}`);
}

function extractList(res, tab) {
  const data = pickData(res);
  const keys = listKey(tab);
  const list = keys.map((key) => data[key]).find((value) => Array.isArray(value))
    || (Array.isArray(data.list) ? data.list : (Array.isArray(data.items) ? data.items : []));
  const total = Number(res?.total ?? data.total_count ?? data.totalElements ?? list.length) || list.length;
  const lastSyncedAt = res?.lastSyncedAt || data.lastSyncedAt || null;
  const neverSynced = Boolean(res?.neverSynced ?? data.neverSynced)
    || (!lastSyncedAt && list.length === 0);
  return { list, total, lastSyncedAt, neverSynced };
}

function toExportRows(tab, source) {
  if (tab === 'statements') {
    return source.map((row) => ({
      statementId: row.id || row.statement_id || '',
      paymentStatus: row.payment_status || row.status || '',
      settlementAmount: moneyOf(row, 'settlement_amount'),
      revenueAmount: moneyOf(row, 'revenue_amount'),
      feeAmount: moneyOf(row, 'fee_amount'),
      adjustmentAmount: moneyOf(row, 'adjustment_amount'),
      paymentId: row.payment_id || '',
      statementTime: formatTime(row.statement_time || row.statementTime || row.payment_time),
    }));
  }
  if (tab === 'withdrawals') {
    return source.map((row) => ({
      id: row.id || '',
      type: i18n.t({ WITHDRAW: 'finance.withdraw', SETTLE: 'finance.settleIn', TRANSFER: 'finance.transfer', REVERSE: 'finance.reverse' }[row.type] || row.type, { ns: 'workbench', defaultValue: row.type || '' }),
      status: row.status || '',
      amount: moneyOf(row, 'amount'),
      currency: row.currency || '',
      createTime: formatTime(row.create_time ?? row.createTime),
    }));
  }
  return source.map((row) => ({
    paymentId: row.id || row.payment_id || '',
    status: row.status || row.payment_status || '',
    amount: moneyOf(row, 'amount'),
    settlementAmount: moneyOf(row, 'settlement_amount'),
    reserveAmount: moneyOf(row, 'reserve_amount'),
    bankAccount: row.bank_account || '',
    createTime: formatTime(row.create_time ?? row.createTime),
    paidTime: formatTime(row.paid_time ?? row.paidTime),
  }));
}

function exportHeaders(tab) {
  if (tab === 'statements') {
    return [
      'statementId',
      'paymentStatus',
      'settlementAmount',
      'revenueAmount',
      'feeAmount',
      'adjustmentAmount',
      'paymentId',
      'statementTime',
    ];
  }
  if (tab === 'withdrawals') {
    return ['id', 'type', 'status', 'amount', 'currency', 'createTime'];
  }
  return [
    'paymentId',
    'status',
    'amount',
    'settlementAmount',
    'reserveAmount',
    'bankAccount',
    'createTime',
    'paidTime',
  ];
}

/**
 * 财务账单：付款、结算单、提现（TikTok Finance 本地缓存）。
 */
export default function TiktokPaymentsPage() {
  const { t } = useTranslation('workbench');
  const {
    loading: shopLoading,
    concreteScopeId,
    shopDimensionTabs,
  } = useTiktokPageShopScope();
  const shopId = concreteScopeId || '';
  const initialRange = useMemo(() => defaultRange(), []);

  const [tab, setTab] = useState('payments');
  const [filterDraft, setFilterDraft] = useState({
    startDate: initialRange.start,
    endDate: initialRange.end,
    paymentStatus: '',
    withdrawType: 'WITHDRAW',
  });
  const [applied, setApplied] = useState({
    startDate: initialRange.start,
    endDate: initialRange.end,
    paymentStatus: '',
    withdrawType: 'WITHDRAW',
  });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [neverSynced, setNeverSynced] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [txRows, setTxRows] = useState([]);
  const [txStatementId, setTxStatementId] = useState('');
  const [orderTx, setOrderTx] = useState(null);
  const unsettledTokens = useRef({ 1: '' });
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const [txLoading, setTxLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, tab, index)).filter(Boolean),
    [rows, tab],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = useCallback(() => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  }, [selectableKeys, allSelected]);

  const toggleOne = useCallback((key, row) => {
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    rows.forEach((row, index) => {
      const key = rowKey(row, tab, index);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys, tab]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [shopId, tab, applied]);

  const resolveSelectedRows = useCallback(() => (
    [...selectedKeys]
      .map((key) => selectedRowsRef.current.get(key))
      .filter(Boolean)
  ), [selectedKeys]);

  const loadList = useCallback(async (sid, nextTab, pageNo, size, options = {}) => {
    if (!sid) {
      setRows([]);
      setTotal(0);
      setNeverSynced(true);
      setLastSyncedAt(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const query = {
        cached: nextTab !== 'unsettled',
        page: Math.max(1, Number(pageNo) || 1),
        pageSize: Math.max(1, Number(size) || TIKTOK_OPS_DEFAULT_PAGE_SIZE),
      };
      let res;
      if (nextTab === 'statements') {
        res = await tiktokPlatformAPI.listFinanceStatements(sid, query);
      } else if (nextTab === 'withdrawals') {
        res = await tiktokPlatformAPI.listFinanceWithdrawals(sid, query);
      } else if (nextTab === 'unsettled') {
        const pageIndex = Math.max(1, Number(pageNo) || 1);
        const range = appliedRef.current || {};
        const start = range.startDate ? Math.floor(Date.parse(`${range.startDate}T00:00:00+08:00`) / 1000) : undefined;
        const end = range.endDate ? Math.floor(Date.parse(`${range.endDate}T23:59:59+08:00`) / 1000) : undefined;
        res = await tiktokPlatformAPI.listUnsettledTransactions(sid, {
          pageSize: query.pageSize,
          pageToken: pageIndex <= 1 ? undefined : unsettledTokens.current[pageIndex],
          searchTimeGe: Number.isFinite(start) ? start : undefined,
          searchTimeLt: Number.isFinite(end) ? end : undefined,
        });
        const nextToken = pickData(res)?.next_page_token || '';
        if (nextToken) unsettledTokens.current[pageIndex + 1] = nextToken;
      } else {
        res = await tiktokPlatformAPI.listFinancePayments(sid, query);
      }
      const payload = extractList(res, nextTab);
      setRows(payload.list);
      setTotal(payload.total);
      setLastSyncedAt(payload.lastSyncedAt || (options.fromSync ? new Date().toISOString() : null));
      setNeverSynced(nextTab === 'unsettled' ? false : (options.fromSync ? false : payload.neverSynced));
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('finance.loadFinanceFailed'));
      showToast(e?.message || t('finance.loadFinanceFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    setPage(1);
    setTxRows([]);
    setTxStatementId('');
  }, [shopId, tab]);

  useEffect(() => {
    if (!shopId || shopLoading) return undefined;
    loadList(shopId, tab, page, pageSize);
    return undefined;
  }, [shopId, shopLoading, tab, page, pageSize, applied, loadList]);

  const onQuery = () => {
    setPage(1);
    setApplied({ ...filterDraft });
  };

  const onReset = () => {
    const range = defaultRange();
    const next = {
      startDate: range.start,
      endDate: range.end,
      paymentStatus: '',
      withdrawType: 'WITHDRAW',
    };
    setFilterDraft(next);
    setPage(1);
    setApplied(next);
  };

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('finance.pickTiktok'), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'finance',
        shopId,
        label: t('publishRecord.syncUpdate'),
        payload: {
          startDate: applied.startDate,
          endDate: applied.endDate,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      setPage(1);
      await loadList(shopId, tab, 1, pageSize, { fromSync: true });
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      if (e?.message) showToast(e.message, 'error');
    }
  }, [shopId, applied, tab, pageSize, loadList, startJob, onTrackDone, t]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync, { minIntervalMs: 5000 });

  const syncing = syncingLocal || isBusy('finance', shopId);

  const openTransactions = async (statementId) => {
    if (!shopId || !statementId) return;
    setTxLoading(true);
    setTxStatementId(String(statementId));
    try {
      const tx = await tiktokPlatformAPI.listStatementTransactions(shopId, statementId, { pageSize: 50 });
      const data = pickData(tx);
      const list = Array.isArray(data.statement_transactions)
        ? data.statement_transactions
        : (Array.isArray(data.transactions) ? data.transactions : []);
      setTxRows(list);
    } catch (e) {
      setTxRows([]);
      showToast(e?.message || t('finance.loadTxFailed'), 'error');
    } finally {
      setTxLoading(false);
    }
  };

  const exportRows = useCallback((source) => {
    if (!source?.length) {
      showToast(t('finance.noneExportData'), 'warning');
      return;
    }
    const prefix = tab === 'statements'
      ? 'tiktok-finance-statements'
      : (tab === 'withdrawals' ? 'tiktok-finance-withdrawals' : 'tiktok-finance-payments');
    downloadCsv(`${prefix}-${Date.now()}.csv`, toExportRows(tab, source), exportHeaders(tab));
    showToast(t('tiktokCatalog.exported', { count: source.length }), 'success');
  }, [tab, t]);

  const exportFiltered = useCallback(async () => {
    if (!shopId || exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const query = { cached: true, page: pageNo + 1, pageSize: size };
        const res = tab === 'statements'
          ? await tiktokPlatformAPI.listFinanceStatements(shopId, query)
          : (tab === 'withdrawals'
            ? await tiktokPlatformAPI.listFinanceWithdrawals(shopId, query)
            : await tiktokPlatformAPI.listFinancePayments(shopId, query));
        const payload = extractList(res, tab);
        return { items: payload.list, total: payload.total };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, tab, exportRows, t]);

  const lastSyncedLabel = useMemo(() => {
    if (syncing || loading) return t('productAction.syncing');
    if (!lastSyncedAt) return t('finance.notSyncedHint');
    try {
      return t('finance.lastSync', { time: formatSiteListSyncedAt(lastSyncedAt) });
    } catch {
      return t('finance.lastSync', { time: String(lastSyncedAt) });
    }
  }, [syncing, loading, lastSyncedAt, t]);

  const emptyTitle = useMemo(() => {
    if (!shopId) return t('returnList.pickShop');
    if (loading) return t('returnList.loading');
    if (neverSynced) return t('finance.neverSynced');
    return t('finance.emptyFilter');
  }, [shopId, loading, neverSynced, t]);

  const emptyHint = useMemo(() => {
    if (!shopId) return t('finance.pickShopHint');
    if (neverSynced) return t('finance.neverSyncedHint');
    return null;
  }, [shopId, neverSynced, t]);

  const colSpan = (tab === 'statements' ? 9 : (tab === 'unsettled' ? 6 : (tab === 'withdrawals' ? 6 : 8))) + 1;

  const dimensionTabs = useMemo(() => (
    <div className="tko-status-tabs" role="tablist" aria-label={t('finance.dimAria')}>
      {FINANCE_TABS.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={tab === item.id}
          className={`tko-status-tab${tab === item.id ? ' tko-status-tab--active' : ''}`}
          onClick={() => setTab(item.id)}
        >
          {t(item.labelKey)}
        </button>
      ))}
    </div>
  ), [tab, t]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, dimensionTabs),
    [shopDimensionTabs, dimensionTabs],
  );

  const renderCheckTh = () => (
    <th
      scope="col"
      className="tko-ops-th tko-ops-th--check tiktok-product-cell--spu-check"
      onClick={() => {
        if (!shopId || loading || selectableKeys.length === 0) return;
        toggleAll();
      }}
    >
      <input
        type="checkbox"
        aria-label={t('finance.selectPage')}
        disabled={!shopId || loading || selectableKeys.length === 0}
        checked={allSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={toggleAll}
      />
    </th>
  );

  const renderCheckTd = (key, row) => (
    <td
      className="tko-ops-td tko-ops-td--check tiktok-product-cell--spu-check"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        aria-label={t('finance.selectRow')}
        checked={selectedKeys.has(key)}
        disabled={!shopId || loading}
        onChange={() => toggleOne(key, row)}
      />
    </td>
  );

  return (
    <>
      <TiktokSyncProgressSlot kinds={['finance']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || syncing || exporting || shopLoading}
            syncKinds={['finance']}
            onSync={onSync}
            syncing={syncing || loading}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing || loading}
            lastSyncedLabel={lastSyncedLabel}
            syncLabel={t('finance.pullLabel')}
            syncHint={t('finance.pullHint')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(resolveSelectedRows()),
            })}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <TiktokDateRangeFilter
              showTimeType={false}
              timeTypeLabel={t('finance.time')}
              startDate={filterDraft.startDate}
              endDate={filterDraft.endDate}
              disabled={!shopId}
              onStartDateChange={(v) => setFilterDraft((prev) => ({ ...prev, startDate: v }))}
              onEndDateChange={(v) => setFilterDraft((prev) => ({ ...prev, endDate: v }))}
            />
            {tab === 'statements' ? (
              <div className="tko-field tko-field--inline tko-field--status">
                <span className="tko-field__label">{t('finance.payStatus')}</span>
                <select
                  className="tko-select tko-select--status tko-select--compact"
                  value={filterDraft.paymentStatus}
                  disabled={!shopId}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, paymentStatus: e.target.value }))}
                >
                  <option value="">{t('tiktokCatalog.all')}</option>
                  <option value="PAID">{t('finance.stCheck3')}</option>
                  <option value="PROCESSING">{t('finance.processing')}</option>
                  <option value="FAILED">{t('certLib.failedShort')}</option>
                </select>
              </div>
            ) : null}
            {tab === 'withdrawals' ? (
              <div className="tko-field tko-field--inline tko-field--status">
                <span className="tko-field__label">{t('finance.type')}</span>
                <select
                  className="tko-select tko-select--status tko-select--compact"
                  value={filterDraft.withdrawType}
                  disabled={!shopId}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, withdrawType: e.target.value }))}
                >
                  {WITHDRAW_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>{t(item.labelKey)}</option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--secondary tko-btn--sm"
              disabled={!shopId}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? <div className="tko-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('finance.queryingPay')}
      >
        <div className="tko-drafts__table-wrap tko-ops-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--sticky-head">
            <colgroup>
              <col className="tko-ops-col tko-ops-col--check tiktok-product-col--check" />
              {tab === 'statements' ? (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="tko-ops-col tko-ops-col--ops" />
                </>
              ) : null}
              {tab === 'withdrawals' ? (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="tko-ops-col tko-ops-col--absorb" />
                </>
              ) : null}
              {tab === 'payments' ? (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="tko-ops-col tko-ops-col--absorb" />
                </>
              ) : null}
              {tab === 'unsettled' ? (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="tko-ops-col tko-ops-col--absorb" />
                </>
              ) : null}
            </colgroup>
            <thead>
              {tab === 'statements' ? (
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{t('finance.statementNo')}</th>
                  <th scope="col">{t('finance.payStatus')}</th>
                  <th scope="col">{t('finance.settleAmt')}</th>
                  <th scope="col">{t('finance.revenue')}</th>
                  <th scope="col">{t('finance.fee')}</th>
                  <th scope="col">{t('finance.adjustment')}</th>
                  <th scope="col">{t('finance.paymentNo')}</th>
                  <th scope="col">{t('finance.settleTime')}</th>
                  <th scope="col">{t('finance.tx')}</th>
                </tr>
              ) : null}
              {tab === 'withdrawals' ? (
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{t('finance.docNo')}</th>
                  <th scope="col">{t('finance.type')}</th>
                  <th scope="col">{t('finance.status')}</th>
                  <th scope="col">{t('finance.amount')}</th>
                  <th scope="col">{t('finance.currency')}</th>
                  <th scope="col">{t('productTemplate.colCreatedAt')}</th>
                </tr>
              ) : null}
              {tab === 'payments' ? (
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{t('finance.paymentNo')}</th>
                  <th scope="col">{t('finance.status')}</th>
                  <th scope="col">{t('finance.payAmt')}</th>
                  <th scope="col">{t('finance.settleAmt')}</th>
                  <th scope="col">{t('finance.reserve')}</th>
                  <th scope="col">{t('finance.bank')}</th>
                  <th scope="col">{t('productTemplate.colCreatedAt')}</th>
                  <th scope="col">{t('finance.payTime')}</th>
                </tr>
              ) : null}
              {tab === 'unsettled' ? (
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{t('finance.orderAdjust')}</th>
                  <th scope="col">{t('finance.type')}</th>
                  <th scope="col">{t('finance.estSettle')}</th>
                  <th scope="col">{t('finance.estRevenue')}</th>
                  <th scope="col">{t('finance.unsettledReason')}</th>
                  <th scope="col">{t('finance.orderTime')}</th>
                </tr>
              ) : null}
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="wf-tasks-table__empty">
                    <div className="funding-empty-state">
                      <p className="funding-empty-state__title">{emptyTitle}</p>
                      {emptyHint ? (
                        <p className="funding-empty-state__description">{emptyHint}</p>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : null}
              {tab === 'payments' && rows.map((row, idx) => {
                const key = rowKey(row, tab, idx);
                return (
                  <tr key={key}>
                    {renderCheckTd(key, row)}
                    <td className="tko-ops-td tko-ops-td--code">{key}</td>
                    <td>{paymentStatusTag(row.status || row.payment_status)}</td>
                    <td>{moneyOf(row, 'amount')}</td>
                    <td>{moneyOf(row, 'settlement_amount')}</td>
                    <td>{moneyOf(row, 'reserve_amount')}</td>
                    <td>{row.bank_account || '—'}</td>
                    <td>{formatTime(row.create_time ?? row.createTime)}</td>
                    <td>{formatTime(row.paid_time ?? row.paidTime)}</td>
                  </tr>
                );
              })}
              {tab === 'statements' && rows.map((row, idx) => {
                const key = rowKey(row, tab, idx);
                return (
                  <tr key={key}>
                    {renderCheckTd(key, row)}
                    <td className="tko-ops-td tko-ops-td--code">{key}</td>
                    <td>{paymentStatusTag(row.payment_status || row.status)}</td>
                    <td>{moneyOf(row, 'settlement_amount')}</td>
                    <td>{moneyOf(row, 'revenue_amount')}</td>
                    <td>{moneyOf(row, 'fee_amount')}</td>
                    <td>{moneyOf(row, 'adjustment_amount')}</td>
                    <td>{row.payment_id || '—'}</td>
                    <td>{formatTime(row.statement_time || row.statementTime || row.payment_time)}</td>
                    <td>
                      <button
                        type="button"
                        className="tko-btn tko-btn--secondary tko-btn--sm"
                        disabled={txLoading}
                        onClick={() => openTransactions(key)}
                      >
                        {t('finance.viewTx')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {tab === 'unsettled' && rows.map((row, idx) => {
                const key = rowKey(row, tab, idx);
                const orderId = row.order_id || row.adjustment_order_id || '';
                return (
                  <tr key={key}>
                    {renderCheckTd(key, row)}
                    <td className="tko-ops-td tko-ops-td--code">{orderId || row.adjustment_id || key}</td>
                    <td>{row.type || '—'}</td>
                    <td>{moneyOf(row, 'est_settlement_amount')}</td>
                    <td>{moneyOf(row, 'est_revenue_amount')}</td>
                    <td>{row.unsettled_reason || '—'}</td>
                    <td>{formatTime(row.order_create_time)}</td>
                  </tr>
                );
              })}
              {tab === 'withdrawals' && rows.map((row, idx) => {
                const key = rowKey(row, tab, idx);
                const typeKey = { WITHDRAW: 'finance.withdraw', SETTLE: 'finance.settleIn', TRANSFER: 'finance.transfer', REVERSE: 'finance.reverse' }[row.type];
                const typeLabel = typeKey ? t(typeKey) : (row.type || '—');
                return (
                  <tr key={key}>
                    {renderCheckTd(key, row)}
                    <td className="tko-ops-td tko-ops-td--code">{key}</td>
                    <td>{typeLabel}</td>
                    <td>{paymentStatusTag(row.status)}</td>
                    <td>{moneyOf(row, 'amount')}</td>
                    <td>{row.currency || '—'}</td>
                    <td>{formatTime(row.create_time ?? row.createTime)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {tab === 'statements' && txStatementId ? (
          <section className="tko-board-section">
            <h3 className="tko-board-section__title">
              {t('finance.txTitle', { id: txStatementId, suffix: txLoading ? t('finance.txLoading') : t('finance.txCount', { count: txRows.length }) })}
            </h3>
            <div className="tko-drafts__table-wrap tko-ops-table-wrap">
              <table className="tko-publish__table tko-drafts__table tko-ops-table">
                <thead>
                  <tr>
                    <th scope="col">{t('finance.txNo')}</th>
                    <th scope="col">{t('finance.order')}</th>
                    <th scope="col">{t('finance.type')}</th>
                    <th scope="col">{t('finance.settleAmt')}</th>
                    <th scope="col">{t('finance.revenue')}</th>
                    <th scope="col">{t('finance.fee')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!txLoading && txRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="wf-tasks-table__empty">{t('finance.noTx')}</td>
                    </tr>
                  ) : null}
                  {txRows.map((tx, idx) => (
                    <tr key={tx.id || tx.transaction_id || idx}>
                      <td>{tx.id || tx.transaction_id || '—'}</td>
                      <td>
                        {tx.order_id ? (
                          <button
                            type="button"
                            className="tko-btn tko-btn--ghost tko-btn--sm"
                            onClick={async () => {
                              try {
                                const res = await tiktokPlatformAPI.getOrderStatementTransactions(shopId, tx.order_id);
                                const data = pickData(res);
                                setOrderTx({
                                  orderId: tx.order_id,
                                  rows: Array.isArray(data.sku_transactions)
                                    ? data.sku_transactions
                                    : (Array.isArray(data.statement_transactions) ? data.statement_transactions : [data]),
                                });
                              } catch (e) {
                                showToast(e?.message || t('finance.loadOrderFailed'), 'error');
                              }
                            }}
                          >
                            {tx.order_id}
                          </button>
                        ) : (tx.adjustment_order_id || '—')}
                      </td>
                      <td>{tx.type || tx.transaction_type || '—'}</td>
                      <td>{moneyOf(tx, 'settlement_amount')}</td>
                      <td>{moneyOf(tx, 'revenue_amount')}</td>
                      <td>{moneyOf(tx, 'fee_amount')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
        {orderTx ? (
          <section className="tko-board-section">
            <h3 className="tko-board-section__title">{t('finance.orderSkuTitle', { id: orderTx.orderId })}</h3>
            <div className="tko-drafts__table-wrap tko-ops-table-wrap">
              <table className="tko-publish__table tko-drafts__table tko-ops-table">
                <thead>
                  <tr>
                    <th scope="col">SKU</th>
                    <th scope="col">{t('finance.settleAmt')}</th>
                    <th scope="col">{t('finance.revenue')}</th>
                    <th scope="col">{t('finance.fee')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(orderTx.rows || []).map((row, idx) => (
                    <tr key={row.sku_id || row.id || idx}>
                      <td>{row.sku_id || row.sku_name || row.id || '—'}</td>
                      <td>{moneyOf(row, 'settlement_amount')}</td>
                      <td>{moneyOf(row, 'revenue_amount')}</td>
                      <td>{moneyOf(row, 'fee_amount')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('finance.pagerPay')}
        />
      </ProductListCard>
    </>
  );
}
